import { Component, DestroyRef, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FeedbackService } from '../../../core/services/feedback.service';
import { FeedbackDto, FeedbackType } from '../../../core/models/feedback.models';
import {
  LucideAngularModule,
  Bug,
  Lightbulb,
  Heart,
  MessageSquare,
  RefreshCw,
  CheckCheck,
  Filter,
  Clock,
} from 'lucide-angular';

@Component({
  selector: 'app-admin-feedbacks',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, TranslatePipe],
  templateUrl: './feedbacks.component.html',
})
export class AdminFeedbacksComponent implements OnInit {
  private feedbackService = inject(FeedbackService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  feedbacks = signal<FeedbackDto[]>([]);
  loading = signal(true);
  filterUnread = signal(false);
  markingId = signal<string | null>(null);

  Bug = Bug;
  Lightbulb = Lightbulb;
  Heart = Heart;
  MessageSquare = MessageSquare;
  RefreshCw = RefreshCw;
  CheckCheck = CheckCheck;
  Filter = Filter;
  Clock = Clock;

  unreadCount = computed(() => this.feedbacks().filter((f) => !f.isRead).length);

  filtered = computed(() =>
    this.filterUnread() ? this.feedbacks().filter((f) => !f.isRead) : this.feedbacks(),
  );

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading.set(true);
    this.feedbackService
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) this.feedbacks.set(res.data);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  markRead(id: string) {
    this.markingId.set(id);
    this.feedbackService
      .markRead(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.feedbacks.update((list) =>
            list.map((f) => (f.id === id ? { ...f, isRead: true } : f)),
          );
          this.markingId.set(null);
        },
        error: () => this.markingId.set(null),
      });
  }

  markAllRead() {
    const unread = this.feedbacks().filter((f) => !f.isRead);
    unread.forEach((f) => this.markRead(f.id));
  }

  typeLabel(type: FeedbackType): string {
    switch (type) {
      case 'Bug':
        return this.translate.instant('FEEDBACK.TYPES.BUG');
      case 'Suggestion':
        return this.translate.instant('FEEDBACK.TYPES.SUGGESTION');
      case 'Praise':
        return this.translate.instant('FEEDBACK.TYPES.PRAISE');
    }
  }

  typeIcon(type: FeedbackType) {
    switch (type) {
      case 'Bug':
        return this.Bug;
      case 'Suggestion':
        return this.Lightbulb;
      case 'Praise':
        return this.Heart;
    }
  }

  typeColor(type: FeedbackType): string {
    switch (type) {
      case 'Bug':
        return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
      case 'Suggestion':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
      case 'Praise':
        return 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400';
    }
  }

  timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'Hozir';
    if (m < 60) return `${m} daqiqa oldin`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} soat oldin`;
    const d = Math.floor(h / 24);
    return `${d} kun oldin`;
  }
}
